import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  SpeakerWaveIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  SignalIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';

type AuthMode = 'signin' | 'signup' | 'confirm';

const Header: React.FC = () => {
  const location = useLocation();
  const { user, login, register, confirmSignUp, logout, isAuthenticated } = useUser();
  const { showSuccess, showError } = useToast();

  const [showPanel, setShowPanel] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [form, setForm] = useState({ email: '', password: '', code: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navItems = [
    { path: '/assessment', label: 'Assessment', icon: SpeakerWaveIcon },
    { path: '/hearing-test', label: 'Hearing Test', icon: SignalIcon },
    { path: '/questionnaire', label: 'Questionnaire', icon: ClipboardDocumentListIcon },
    { path: '/treatments', label: 'Treatments', icon: HeartIcon },
    { path: '/my-results', label: 'My Results', icon: ChartBarIcon },
  ];

  const close = () => {
    setShowPanel(false);
    setAuthMode('signin');
    setForm({ email: '', password: '', code: '' });
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(form.email, form.password);
      showSuccess('Welcome back!', 'You have successfully signed in.');
      close();
    } catch (err: any) {
      showError('Sign in failed', err.message ?? 'Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await register(form.email, form.password);
      setAuthMode('confirm');
      showSuccess('Account created!', 'Check your email for a confirmation code.');
    } catch (err: any) {
      showError('Sign up failed', err.message ?? 'Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await confirmSignUp(form.email, form.code);
      setAuthMode('signin');
      showSuccess('Email confirmed!', 'You can now sign in.');
    } catch (err: any) {
      showError('Confirmation failed', err.message ?? 'Invalid code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-auris-teal rounded-lg flex items-center justify-center">
              <SpeakerWaveIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-auris-dark">TinniTrack</span>
            <span className="text-sm text-gray-500">by Auris</span>
          </Link>

          {/* Navigation */}
          <nav className="flex space-x-8">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-auris-teal bg-auris-blue bg-opacity-20'
                      : 'text-gray-600 hover:text-auris-teal hover:bg-auris-blue hover:bg-opacity-10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User menu */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <UserIcon className="w-4 h-4" />
                  <span>{user?.email}</span>
                </div>
                <button
                  onClick={logout}
                  className="flex items-center space-x-1 text-sm text-gray-600 hover:text-auris-teal transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => { setAuthMode('signin'); setShowPanel(!showPanel); }}
                  className="text-sm text-gray-600 hover:text-auris-teal transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => { setAuthMode('signup'); setShowPanel(!showPanel); }}
                  className="bg-auris-teal text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auth panel */}
      {showPanel && !isAuthenticated && (
        <div className="absolute top-16 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-5 w-80 z-50">
          {/* Tabs */}
          {authMode !== 'confirm' && (
            <div className="flex mb-4 border-b border-gray-200">
              {(['signin', 'signup'] as AuthMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setAuthMode(mode)}
                  className={`flex-1 pb-2 text-sm font-medium transition-colors ${
                    authMode === mode
                      ? 'border-b-2 border-auris-teal text-auris-teal'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {mode === 'signin' ? 'Sign In' : 'Sign Up'}
                </button>
              ))}
            </div>
          )}

          {/* Sign In */}
          {authMode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-3">
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-auris-teal focus:border-transparent"
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-auris-teal focus:border-transparent"
                required
              />
              <div className="flex space-x-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-auris-teal text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Signing in…' : 'Sign In'}
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Sign Up */}
          {authMode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-3">
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-auris-teal focus:border-transparent"
                required
              />
              <input
                type="password"
                placeholder="Password (min 8 chars)"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-auris-teal focus:border-transparent"
                minLength={8}
                required
              />
              <div className="flex space-x-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-auris-teal text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Creating account…' : 'Create Account'}
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Confirm code */}
          {authMode === 'confirm' && (
            <form onSubmit={handleConfirm} className="space-y-3">
              <p className="text-sm text-gray-600">
                Enter the confirmation code sent to <strong>{form.email}</strong>.
              </p>
              <input
                type="text"
                placeholder="Confirmation code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-auris-teal focus:border-transparent"
                required
              />
              <div className="flex space-x-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-auris-teal text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Confirming…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
